/**
 * The Edward Jones Quick Plan form, shown by `QuickPlanDialog` instead of the standard Quick Plan
 * when `useQuickPlanAvailability().isEdjQuickPlan` is true.
 *
 * It asks for seven things: first and last name, date of birth, sex, ZIP, Married / Not married
 * and net worth. There is no smoking question and no partner, because the EDJ model input has
 * neither. Validation lives in `useEdjQuickPlanForm` and submission in `useEdjShortFormIntake`;
 * this component only renders. The disclosure above the button is the one the plan requires
 * whenever a quick estimate is created.
 */
import {
  Alert,
  AlertDescription,
  Button,
  DateInput,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@design-system';
import { CurrencyInput } from '@/shared/components/numeric-input';
import { AlertCircle, ArrowRight } from 'lucide-react';
import { SEX_OPTIONS } from '../hooks/use-quick-plan-form';
import {
  EDJ_MARITAL_STATUS_OPTIONS,
  useEdjQuickPlanForm,
  type EdjQuickPlanFormValues,
} from '../hooks/use-edj-quick-plan-form';
import { useEdjShortFormIntake } from '../hooks/use-edj-short-form-intake';

export const EDJ_QUICK_ESTIMATE_DISCLOSURE =
  'This quick estimate uses limited client information. Results may change when more complete information becomes available.';

interface EdjQuickPlanFormProps {
  advisorId: string;
  onSubmitSuccess?: (data: ShortFormIntakeResponse) => void;
  defaultValues?: Partial<EdjQuickPlanFormValues>;
}

export function EdjQuickPlanForm({ advisorId, onSubmitSuccess, defaultValues }: EdjQuickPlanFormProps) {
  const { submitEdjQuickPlan, isPending } = useEdjShortFormIntake({
    advisorId,
    onSuccess: onSubmitSuccess,
  });
  const { form, onSubmit, isValid } = useEdjQuickPlanForm({
    onSubmit: values => submitEdjQuickPlan(values),
    defaultValues,
  });

  return (
    <Form {...form}>
      <form
        onSubmit={onSubmit}
        className="space-y-6"
        data-testid="edj-quick-plan-form"
      >
        <fieldset
          disabled={isPending}
          className="space-y-6"
        >
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    First name <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Jane"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Last name <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Doe"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="birthDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Date of birth <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <DateInput
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      name={field.name}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="sex"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Sex <span className="text-destructive">*</span>
                  </FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select sex" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="z-99">
                      {SEX_OPTIONS.map(option => (
                        <SelectItem
                          key={option.value}
                          value={option.value}
                        >
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="zipCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Zip code <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="12345"
                      maxLength={5}
                      inputMode="numeric"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="maritalStatus"
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <FormLabel>
                    Marital status <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      value={field.value}
                      className="flex gap-4 pt-2"
                    >
                      {EDJ_MARITAL_STATUS_OPTIONS.map(option => (
                        <FormItem
                          key={option.value}
                          className="flex items-center gap-2 space-y-0"
                        >
                          <FormControl>
                            <RadioGroupItem value={option.value} />
                          </FormControl>
                          <FormLabel className="font-normal">{option.label}</FormLabel>
                        </FormItem>
                      ))}
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="netWorth"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Net worth <span className="text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <CurrencyInput
                    placeholder="0"
                    value={field.value ?? null}
                    onPreview={value => field.onChange(value ?? undefined)}
                    onChange={value => {
                      field.onChange(value ?? undefined);
                      field.onBlur();
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </fieldset>

        <Alert
          variant="info"
          className="gap-x-2! p-3"
        >
          <AlertCircle />
          <AlertDescription>{EDJ_QUICK_ESTIMATE_DISCLOSURE}</AlertDescription>
        </Alert>

        <Button
          type="submit"
          className="w-full"
          disabled={!isValid || isPending}
        >
          {isPending ? 'Working...' : 'Create quick estimate'}
          {!isPending && <ArrowRight />}
        </Button>
      </form>
    </Form>
  );
}
